/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This Page corresponds to the setup of a new password thanks to a token
 * The URI keyword would have to match something like 'nouveau-mot-de-passe'
 */

// Imports
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

// Interfaces
import { AlertType } from '../../../interfaces/IUtils';
import { PublicCabinet } from '../../../interfaces/ICabinets';

// Utils
import { ErrorForUsr, displayAlert, signOut } from '../../utils/Errors';
import { ApiCallConstructor, GlobalApiCall, HTTPResponse, fetchDossiers } from "../../utils/ApiCallConstructor";
import { useIsMobileScreen } from '../../utils/MobileScreenContext';

// Buroclic adaptive components
import { PoweredByBc } from '../../buroclic/PoweredByBc';
import { isAValidPassword } from '../../utils/Format';

export const DefinePwdPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { state } = useLocation();
  const isMobileScreen = useIsMobileScreen();

  const initialPwd: boolean = state?.initialPwd;
  const tmpCabinet: PublicCabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet;
  const [cabinet, setCabinet] = useState<PublicCabinet>(tmpCabinet);

  // Store cabinetAlias in localStorage
  const cabinetAlias = (params.cabinetAlias ? params.cabinetAlias : '');

  if (cabinetAlias !== '')
    localStorage.setItem('cabinetAlias', cabinetAlias);

  document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';

  // Password-attached states
  const [password, setPassword] = useState('');
  const [isPwdShown, setIsPwdShown] = useState<boolean>(false);
  const [passwordConfirmed, setpasswordConfirmed] = useState('');
  const [isPwdConfirmedShown, setIsPwdConfirmedShown] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState('');


  // Initial data
  useEffect(() => {

    if (!initialPwd && !Boolean(localStorage.getItem('isAuthenticated'))) {
      // Retrieve token from URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (token)
        setToken(token);
      else
        displayAlert({ message: 'L\'URL utilisé pour réinitialiser votre mot de passe semble être incomplet', type: AlertType.error });
    }

    if (!tmpCabinet?.host) {
      // Fetch cabinet Back-end data such as Host
      const fetchCabinet = async () => {
        try {
          const cabinetResponse = await toast.promise(
            GlobalApiCall({
              endpoint: `/cabinets/${cabinetAlias}`,
              method: 'GET',
            }),
            {
              pending: 'Liaison avec le cabinet en cours ...',
              error: { render({ data }) { return `Impossible d'établir une laision avec le cabinet : ${(data as HTTPResponse).error}` } },
            });

          // Handle error
          if (cabinetResponse.error !== null)
            throw new Error(cabinetResponse.error);

          const tmpCabinet = (cabinetResponse?.result as PublicCabinet);
          if (!(tmpCabinet.loginBackground)) tmpCabinet.loginBackground = '/images/background.png';
          setCabinet(tmpCabinet);
          localStorage.setItem('cabinet', JSON.stringify(tmpCabinet));
          document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';
        } catch (err: any) {
          localStorage.clear();
          console.error(err.error)
        }
      }

      // load cabinet back-end API
      fetchCabinet();
    }


  }, [cabinetAlias, tmpCabinet, initialPwd]);


  // Is it ok to make an API Call to set the new password ?
  const passwordValidation = () => {
    if (!passwordConfirmed || !password) {
      setErrorMessage('Veuillez compléter l\'intégralité des champs présentés ci-dessus');
      return false;
    }

    if (password !== passwordConfirmed) {
      setErrorMessage('Les mots de passe ne sont pas identiques');
      return false;
    }

    if (!isAValidPassword(password)) {
      setErrorMessage('Votre mot de passe doit contenir au minimum 8 caractères dont à minima 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial');
      return false;
    }

    return true;
  }

  // Validate the password when user edit a field
  useEffect(() => {
    setErrorMessage('');
  }, [password, passwordConfirmed]);


  // Update password in the DB
  const updatePassword = async (e) => {
    e.preventDefault();

    // Check for password validation
    if (!passwordValidation()) return;

    if (initialPwd === true) {

      // Edit the Buroclic-based generated password
      try {
        await toast.promise(
          ApiCallConstructor({
            endpoint: '/account/password',
            method: 'PUT',
            body: {
              oldPassword: state?.oldPassword,
              password: password,
            }
          }),
          {
            pending: 'Sauvegarde de votre nouveau mot de passe en cours',
            error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
            success: 'Votre mot de passe a été modifié'
          });

        // Get dossiers the user has the right to see
        // and navigate to the proper endpoint based on its number of dossiers
        fetchDossiers().then((fetchedDossiers) => {
          if (fetchedDossiers.length <= 0) navigate('/accueil', { state: { error: 'Aucun document n\'a encore été publié sur votre espace client.' } }); // no dossier or document
          else navigate('/accueil'); // redirect to the home page with folders picker
        })
          .catch((err: any) => {
            displayAlert({ message: err.error, type: AlertType.error });
            if (err.error === ErrorForUsr.NotSignedIn)
              signOut((cabAlias) => navigate(`/${cabAlias}/login`));
          });

      } catch (err: any) { console.error(err.error) }


    } else {
      // Hashed password

      if (token === '') {
        displayAlert({ message: 'L\'URL utilisé pour réinitialiser votre mot de passe semble être incomplet', type: AlertType.error });
        return;
      }
      // Save the new pwd in DB
      try {
        await toast.promise(
          ApiCallConstructor({
            endpoint: '/account/first-password',
            method: 'PUT',
            body: {
              password: password,
              token: token,
              dbAlias: cabinet?.dbName
            }
          }),
          {
            pending: 'Sauvegarde de votre nouveau mot de passe en cours',
            error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
            success: 'Votre mot de passe a été modifié'
          });
        // Redirect to login page
        navigate(`/${cabinetAlias}/login`);

      } catch (err: any) { console.error(err.error) }
    }
  };


  // HTML Template
  const renderMobile = () => {
    return (
      <div className={`justify-center flex flex-col rounded-sm m-4`} style={{ width: 'calc(100vw - 3rem)' }} >
        <img src={cabinet?.logo} alt="Company logo" className={`h-full w-full object-contain object-center max-h-96 my-4 mx-auto max-w-[75%]`} />

        {/* Forgotten password form */}
        <form onSubmit={updatePassword} className="bg-[#f7f7f7] border border-black rounded-md mt-8 p-12 pb-0 px-3 pt-2 sm:h-4/5 lg:h-2/3 grow" style={{ width: '-webkit-fill-available' }}>
          <div className='flex flex-col h-full' style={{ height: 'calc(100% - 2rem)' }}>
            <div className='flex flex-col items-start grow'>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-4 w-1/5" />

              {/* Password */}
              <label htmlFor="pwd" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Nouveau mot de passe :</label>
              <div className="border mb-8 flex items-center rounded-md w-full">
                <input type={`${isPwdShown ? 'text' : 'password'}`} id="pwd" placeholder='Mot de passe' value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white py-2 px-4 text-4xl rounded-sm h-24 shadow-bottom outline-none" style={{ width: '-webkit-fill-available' }} />
                <button title='Voir le mot de passe' onClick={() => setIsPwdShown(!isPwdShown)} className="py-3 border-l border-l-gray-300 h-full cursor-pointer">
                  <FontAwesomeIcon className={`h-16 w-16 mx-4`} icon={isPwdShown ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Password confirmation */}
              <label htmlFor="pwdConfirm" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Confirmation :</label>
              <div className="border mb-8 flex items-center rounded-md w-full">
                <input type={`${isPwdConfirmedShown ? 'text' : 'password'}`} id="pwdConfirm" placeholder='Mot de passe' value={passwordConfirmed} onChange={(e) => setpasswordConfirmed(e.target.value)}
                  className="bg-white py-2 px-4 text-4xl rounded-sm h-24 shadow-bottom outline-none" style={{ width: '-webkit-fill-available' }} />
                <button title='Voir le mot de passe' onClick={() => setIsPwdConfirmedShown(!isPwdConfirmedShown)} className="py-3 border-l border-l-gray-300 h-full cursor-pointer">
                  <FontAwesomeIcon className={`h-16 w-16 mx-4`} icon={isPwdConfirmedShown ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Error */}
              {errorMessage && <p className="text-red-500 mx-auto font-bold text-4xl">{errorMessage}</p>}

              <button className={`text-white mt-4 text-5xl font-bold py-8 px-12 w-4/5 mb-16 rounded-lg shadow-md mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Sauvegarder
              </button>
            </div>

            {/*  Powered by BuroClic */}
            <PoweredByBc className={'text-center py-1'} imgClassName={'h-full w-auto m-auto mb-16'} />

          </div>
        </form>
      </div>
    );
  }

  const renderComputer = () => {
    return (
      <div className="h-full flex items-center justify-center bg-contain bg-right-bottom bg-no-repeat" style={{ backgroundImage: `url('${cabinet.loginBackground ? cabinet.loginBackground : ''}')` }}>
        <div className="w-4/5 justify-center flex flex-col bg-[#f7f7f7] rounded-lg lg:w-1/3 min-h-[40%] h-[40%] lg:mr-auto lg:ml-12 lg:h-[min-content] lg:my-auto">
          <img src={cabinet?.logo} alt="Company logo" className={`h-auto w-full object-contain object-center max-h-[16rem] mt-4 mx-auto max-w-[16rem]`} />
          {/* Forgotten password form */}
          <form onSubmit={updatePassword} className="border border-black rounded-md p-12 sm:m-3 lg:m-12 pb-0 px-3 pt-2 sm:h-4/5 lg:h-2/3 grow" style={{ width: '-webkit-fill-available' }}>
            <div className='flex flex-col items-start grow' style={{ height: 'calc(100% - 2rem)' }}>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-2 lg:w-1/5 w-10" />

              {/* Password */}
              <label htmlFor="pwd" className="text-gray-500 font-bold hidden lg:inline-block">Nouveau mot de passe :</label>
              <div className="border mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                <input type={`${isPwdShown ? 'text' : 'password'}`} id="pwd" placeholder='Mot de passe' value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                <button type='button' onClick={() => setIsPwdShown(!isPwdShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                  <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isPwdShown ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Password confirmation */}
              <label htmlFor="pwdConfirm" className="text-gray-500 font-bold hidden lg:inline-block">Confirmation :</label>
              <div className="border mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                <input type={`${isPwdConfirmedShown ? 'text' : 'password'}`} id="pwdConfirm" placeholder='Mot de passe' value={passwordConfirmed} onChange={(e) => setpasswordConfirmed(e.target.value)}
                  className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                <button type='button' onClick={() => setIsPwdConfirmedShown(!isPwdConfirmedShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                  <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isPwdConfirmedShown ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Error */}
              {errorMessage && <p className="text-red-500 mx-auto font-bold ">{errorMessage}</p>}

              <button className={`text-white text-1xl mt-4 font-bold py-2 px-4 mb-4 px-auto w-52 rounded-lg shadow-lg mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Sauvegarder
              </button>
            </div>

            {/*  Powered by BuroClic */}
            <PoweredByBc className={'h-8 py-1 mx-auto'} imgClassName={'h-full w-auto m-auto mb-8'} />

          </form>
        </div>
      </div>
    );
  }

  return (
    <div className='h-full w-full'>
      {isMobileScreen ? renderMobile() : renderComputer()}
    </div>
  );
};